import { eachDayOfInterval, endOfMonth, isSameDay, isWithinInterval, startOfMonth } from 'date-fns'
import { formatInTimeZone } from 'date-fns-tz'
import { useAtom } from 'jotai'
import { LinkIcon } from 'lucide-react'
import { useContext, useEffect, useMemo, useState } from 'react'

import { DayCard, FieldVisibilityControls, MonthNavigation } from '../components/monthly-overview'
import { useMonthlyOverview } from '../hooks/useMonthlyOverview'

import { currentMonthAtom } from '@/atoms/calendar'
import Loading from '@/components/loading/Loading'
import { Button } from '@/components/ui/button'
import { ActiveTenantContext } from '@/context/common/Context'
import { useEvents } from '@/hooks/useEvents'
import { useUpdateCalendarPreferences } from '@/hooks/requests/useUpdateCalendarPreferences'
import { useUserSettings } from '@/hooks/useUserSettings'
import { showToast } from '@/utils/toastMessage'

const MonthlyOverview = () => {
  const { settings: userSettings } = useUserSettings()
  const { updateCalendarPreferences } = useUpdateCalendarPreferences()
  const { tenant } = useContext(ActiveTenantContext)
  const { events } = useEvents()

  const [currentMonth, setCurrentMonth] = useAtom(currentMonthAtom)
  const [visibleFields, setVisibleFields] = useState<Record<string, boolean>>({})
  const [isMonthSelectorOpen, setIsMonthSelectorOpen] = useState(false)

  const { bookings, dailyNotes, isLoading } = useMonthlyOverview({ month: currentMonth })

  const showDaysWithoutActivities = useMemo(() => {
    if (!tenant?.id) return false
    return userSettings.calendar?.[tenant.id]?.showDaysWithoutActivities ?? false
  }, [tenant?.id, userSettings.calendar])

  const setShowDaysWithoutActivities = (value: boolean) => {
    updateCalendarPreferences({ showDaysWithoutActivities: value })
  }

  useEffect(() => {
    setVisibleFields({})
  }, [tenant?.id])

  const monthStart = startOfMonth(currentMonth)
  const monthEnd = endOfMonth(currentMonth)
  const daysInMonth = eachDayOfInterval({ start: monthStart, end: monthEnd })

  const previousMonth = () => {
    setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1, 1))
  }

  const nextMonth = () => {
    setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 1))
  }

  const handleMonthChange = (monthIndex: string) => {
    const newDate = new Date(currentMonth.getFullYear(), parseInt(monthIndex), 1)
    setCurrentMonth(newDate)
    setIsMonthSelectorOpen(false)
  }

  const handleYearChange = (year: string) => {
    const newDate = new Date(parseInt(year), currentMonth.getMonth(), 1)
    setCurrentMonth(newDate)
    setIsMonthSelectorOpen(false)
  }

  const getBookingsForDay = (day: Date) => {
    return bookings.filter((booking) => isSameDay(new Date(booking.from), day))
  }

  const getEventsForDay = (day: Date) => {
    return events.filter((event) =>
      isWithinInterval(day, {
        start: new Date(event.from),
        end: new Date(event.until),
      })
    )
  }

  const getDailyNoteForDay = (day: Date) => {
    const dayFormatted = formatInTimeZone(day, tenant?.timezone || 'UTC', 'yyyy-MM-dd')
    return dailyNotes.find((note) => note.date === dayFormatted)
  }

  const filteredDays = useMemo(() => {
    return daysInMonth.filter((day) => {
      if (showDaysWithoutActivities) return true
      const dayBookings = getBookingsForDay(day)
      const dayEvents = getEventsForDay(day)
      const dayNote = getDailyNoteForDay(day)
      return dayBookings.length > 0 || dayEvents.length > 0 || dayNote
    })
  }, [daysInMonth, showDaysWithoutActivities, bookings, events, dailyNotes])

  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(window.location.href)
      showToast({ severity: 'success', detail: 'Link in Zwischenablage kopiert' })
    } catch (error) {
      console.error('Failed to copy link:', error)
      showToast({ severity: 'error', detail: 'Fehler beim Kopieren des Links' })
    }
  }

  return (
    <div className="flex h-full flex-col gap-4 p-4">
      <div className="flex flex-col items-center justify-between gap-5 md:flex-row">
        <MonthNavigation
          currentMonth={currentMonth}
          onPreviousMonth={previousMonth}
          onNextMonth={nextMonth}
          onMonthChange={handleMonthChange}
          onYearChange={handleYearChange}
          isMonthSelectorOpen={isMonthSelectorOpen}
          setIsMonthSelectorOpen={setIsMonthSelectorOpen}
        />
        <div className="flex items-center">
          <FieldVisibilityControls
            showDaysWithoutActivities={showDaysWithoutActivities}
            onShowDaysWithoutActivitiesChange={setShowDaysWithoutActivities}
            visibleFields={visibleFields}
            onVisibleFieldsChange={setVisibleFields}
          />
          <Button
            className="ml-3 h-9 w-9"
            variant="outline"
            size="sm"
            onClick={() => handleCopyLink()}
          >
            <LinkIcon />
          </Button>
        </div>
      </div>

      <Loading loading={isLoading} label="Übersicht wird geladen">
        <div className="grid grid-cols-1 gap-2">
          {filteredDays.length === 0 && (
            <div className="text-muted-foreground text-center text-sm">
              Keine Aktivitäten für diesen Monat.
            </div>
          )}
          {filteredDays.map((day) => {
            const dayBookings = getBookingsForDay(day)
            const dayEvents = getEventsForDay(day)
            const dayNote = getDailyNoteForDay(day)

            return (
              <DayCard
                key={day.toISOString()}
                day={day}
                bookings={dayBookings}
                events={dayEvents}
                dailyNote={dayNote}
                visibleFields={visibleFields}
              />
            )
          })}
        </div>
      </Loading>
    </div>
  )
}

export default MonthlyOverview
